import React from 'react';
import { Hotel, Clock } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function nightsBetween(a, b) {
  if (!a || !b) return null;
  const n = Math.round((new Date(b) - new Date(a)) / 86400000);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * A hotel option saved to an accommodation sector while the trip awaits approval.
 * Nothing here is booked — the card exists to make that unmistakable to whoever is looking,
 * whether that's the person who saved it or the approver reviewing the request.
 */
export default function ProposedHotelCard({ option, approved = false, actions = null }) {
  if (!option) return null;

  const nights = nightsBetween(option.checkIn, option.checkOut);
  const price  = parseFloat(option.price);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <Hotel size={15} aria-hidden="true" className="text-amber-700 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-amber-800">
              Hotel option — not booked
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
              <Clock size={9} aria-hidden="true" />
              {approved ? 'Awaiting re-check' : 'Pending approval'}
            </span>
          </div>

          <p className="mt-1.5 text-sm font-semibold text-gray-900">{option.propertyName}</p>

          <div className="mt-1 space-y-0.5 text-xs text-gray-700">
            {(option.checkIn || option.checkOut) && (
              <p>
                {fmtDate(option.checkIn)} → {fmtDate(option.checkOut)}
                {nights != null && ` · ${nights} night${nights !== 1 ? 's' : ''}`}
              </p>
            )}
            {(option.roomName || option.boardName) && (
              <p>{[option.roomName, option.boardName].filter(Boolean).join(' · ')}</p>
            )}
            <p>
              <span className={option.isRefundable ? 'text-green-700' : 'text-red-700'}>
                {option.isRefundable ? 'Refundable' : 'Non-refundable'}
              </span>
              {option.savedAt && <span className="text-gray-600"> · quoted {fmtDate(option.savedAt)}</span>}
              {option.savedByName && <span className="text-gray-600"> by {option.savedByName}</span>}
            </p>
          </div>

          {!isNaN(price) && (
            <p className="mt-2 text-base font-bold text-gray-900">
              {option.currency || 'AUD'} {price.toFixed(2)}
              <span className="ml-1 text-xs font-normal text-gray-700">total stay, incl. GST</span>
            </p>
          )}

          <p className="mt-2 text-xs leading-relaxed text-amber-900">
            {approved
              ? 'This trip is approved. Re-check live availability and pricing before booking — the rate above was quoted earlier and may have changed.'
              : 'This price is included in the approval request. No room is held and no payment has been taken. Booking unlocks once the trip is approved.'}
          </p>

          {actions && <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
